import { Check, CircleOff } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { KpiTile, Monogram, Note, Panel } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { errorMessage } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatUsd, numberFmt } from '@/lib/format'
import { type ReferralDashboard, useHandlePayout, useReferralDashboard } from './api'

const monthFmt = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
const monthLabel = (ym: string) => monthFmt.format(new Date(`${ym}-01T00:00:00`))

const REWARD = {
  on_hold: { label: 'On hold', tone: 'warn' },
  credited: { label: 'Credited', tone: 'ok' },
  void: { label: 'Void', tone: '' },
} as const
const PAYOUT = {
  requested: { label: 'To send', tone: 'warn' },
  paid: { label: 'Sent', tone: 'ok' },
  cancelled: { label: 'Cancelled', tone: '' },
} as const

/**
 * The referral program, run by Arva SEO (redesign of 2026-10-16): what referred accounts bring
 * in, the partners who brought them, the PayPal payouts to send (owners and admins), commissions
 * and the months behind them.
 */
export function ReferralsPage() {
  const dashboard = useReferralDashboard()
  const percent = dashboard.data?.commission_percent ?? 20

  return (
    <>
      <PageTitle
        title="Referrals"
        description={`Partners earn ${percent}% of a referred account’s first plan payment, held for 14 days. They spend it on renewals or ask for it by PayPal. Settings are under Announcements and settings › Referral program.`}
      />
      {dashboard.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : dashboard.isError ? (
        <div className="card">
          <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
        </div>
      ) : (
        <Dashboard data={dashboard.data} />
      )}
    </>
  )
}

function Dashboard({ data }: { data: ReferralDashboard }) {
  const t = data.totals
  const waiting = data.payouts.filter((p) => p.status === 'requested')
  const months = [...data.months].sort((a, b) => a.month.localeCompare(b.month))

  return (
    <div className="sec" style={{ gap: 20 }}>
      {!data.enabled && (
        <Note icon={<CircleOff aria-hidden="true" />} title="The referral program is off">
          Owners don’t see it and no new commissions are earned. Turn it on under{' '}
          <Link to="/settings?section=referral">Announcements and settings</Link>.
        </Note>
      )}

      <div className="kpis">
        <KpiTile
          label="Referred sign-ups"
          value={numberFmt.format(t.signups)}
          compare={`${numberFmt.format(t.signups_30_days)} in the last 30 days, from ${numberFmt.format(t.partners)} ${t.partners === 1 ? 'partner' : 'partners'}`}
          spark={months.map((m) => m.signups)}
        />
        <KpiTile
          label="Became customers"
          value={numberFmt.format(t.customers)}
          compare={t.conversion !== null ? `${t.conversion}% of referred sign-ups` : 'no sign-ups yet'}
          spark={months.map((m) => m.customers)}
        />
        <KpiTile
          label="Their recurring revenue"
          value={formatMoney(t.mrr)}
          compare="a month, from live plans"
        />
        <KpiTile
          label="Revenue from referred accounts"
          value={formatMoney(t.revenue)}
          compare="everything they’ve paid, before tax"
          spark={months.map((m) => m.revenue)}
        />
        <KpiTile
          label="Partner balances"
          value={formatMoney(t.balances)}
          compare={
            waiting.length > 0
              ? `${waiting.length} PayPal ${waiting.length === 1 ? 'payout' : 'payouts'} requested`
              : 'nothing waiting to be sent'
          }
        />
      </div>

      <div className="split">
        <Panel title="Partners" sub="Owners whose link brought at least one sign-up, most customers first">
          {data.partners.length === 0 ? (
            <p className="empty-line">
              No referred sign-ups yet. Partners show here once someone signs up through their link.
            </p>
          ) : (
            <div className="tw" style={{ border: 0 }}>
              <table className="t" style={{ minWidth: 620 }}>
                <caption className="sr-only">Partners</caption>
                <thead>
                  <tr>
                    <th>Partner</th>
                    <th className="r">Sign-ups</th>
                    <th className="r">Customers</th>
                    <th className="r">Revenue</th>
                    <th className="r">Earned</th>
                    <th className="r">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.partners.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="who">
                          <Monogram name={p.name ?? '?'} size="sm" />
                          <div>
                            <b>{p.name ?? 'Closed account'}</b>
                            <small>
                              {p.email}
                              {p.code && <span className="mono"> · {p.code}</span>}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td className="r num">{numberFmt.format(p.signups)}</td>
                      <td className="r num">{numberFmt.format(p.customers)}</td>
                      <td className="r num">{formatUsd(p.revenue)}</td>
                      <td className="r num">
                        <b>{formatUsd(p.earned)}</b>
                        {p.on_hold > 0 && <small>{formatUsd(p.on_hold)} on hold</small>}
                      </td>
                      <td className="r num">{formatUsd(p.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
        <Panel
          title="PayPal payouts"
          sub={
            waiting.length > 0
              ? 'Send each from Arva SEO’s PayPal account, then mark it sent with the transaction ID'
              : 'Partners ask for their whole balance once it reaches the minimum'
          }
        >
          {data.payouts.length === 0 ? (
            <p className="empty-line">No payout requests yet.</p>
          ) : (
            data.payouts.map((p) => <PayoutRow key={p.id} payout={p} canPay={data.can_pay} />)
          )}
        </Panel>
      </div>

      <Panel
        title="Commissions"
        sub={`${data.commission_percent}% of each referred account’s first plan payment`}
      >
        <div
          className="grid3"
          style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}
        >
          <Figure
            label="On hold"
            value={formatUsd(t.commission_on_hold)}
            sub="credited after 14 days if the plan is on"
          />
          <Figure
            label="Credited so far"
            value={formatUsd(t.commission_earned)}
            sub={`${formatUsd(t.commission_void)} not credited`}
          />
          <Figure
            label="Paid out by PayPal"
            value={formatUsd(t.paid_out)}
            sub={t.payouts_waiting > 0 ? `${formatUsd(t.payouts_waiting)} waiting` : 'nothing waiting'}
          />
          <Figure
            label="Spent on plans"
            value={formatUsd(t.spent_on_plans)}
            sub="balances used on renewals"
          />
        </div>
        {data.rewards.length === 0 ? (
          <p className="empty-line" style={{ marginTop: 14 }}>
            No commissions yet. They appear when a referred account pays for its first plan.
          </p>
        ) : (
          <div className="tw" style={{ border: 0, marginTop: 14 }}>
            <table className="t" style={{ minWidth: 700 }}>
              <caption className="sr-only">Recent commissions</caption>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Partner</th>
                  <th>Customer</th>
                  <th className="r">First payment</th>
                  <th className="r">Commission</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.rewards.map((r) => (
                  <tr key={r.id}>
                    <td className="m" style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(r.created_at)}
                    </td>
                    <td>{r.partner?.name ?? '—'}</td>
                    <td>
                      {r.customer ? (
                        <Link to={`/customers/${r.customer.id}`}>
                          <b>{r.customer.name}</b>
                        </Link>
                      ) : (
                        '—'
                      )}
                      <small>{r.description}</small>
                    </td>
                    <td className="r num">{formatUsd(r.sale_total)}</td>
                    <td className="r num">
                      <b>{formatUsd(r.commission)}</b>
                    </td>
                    <td>
                      <span className={cn('chip', REWARD[r.status].tone)}>{REWARD[r.status].label}</span>
                      <small>
                        {r.status === 'on_hold' ? `Until ${formatDate(r.hold_until)}` : (r.void_reason ?? '')}
                      </small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel
        title="By month"
        sub="Sign-ups and first plans when they happened; revenue is what referred accounts paid that month"
      >
        <div className="tw" style={{ border: 0 }}>
          <table className="t" style={{ minWidth: 520 }}>
            <caption className="sr-only">Referrals by month</caption>
            <thead>
              <tr>
                <th>Month</th>
                <th className="r">Sign-ups</th>
                <th className="r">Customers</th>
                <th className="r">Revenue</th>
                <th className="r">Commission</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m) => (
                <tr key={m.month}>
                  <td>
                    <b>{monthLabel(m.month)}</b>
                  </td>
                  <td className="r num">{numberFmt.format(m.signups)}</td>
                  <td className="r num">{numberFmt.format(m.customers)}</td>
                  <td className="r num">{formatUsd(m.revenue)}</td>
                  <td className="r num">{formatUsd(m.commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  )
}

function Figure({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="mt" style={{ cursor: 'default' }}>
      <span className="l">{label}</span>
      <span className="v">{value}</span>
      <span className="cmp">{sub}</span>
    </div>
  )
}

function PayoutRow({ payout: p, canPay }: { payout: ReferralDashboard['payouts'][number]; canPay: boolean }) {
  const handle = useHandlePayout()
  const [mode, setMode] = useState<'paid' | 'cancel' | null>(null)
  const [text, setText] = useState('')

  const submit = (e: React.SyntheticEvent) => {
    e.preventDefault()
    if (!mode) return
    handle.mutate(
      mode === 'paid'
        ? { id: p.id, action: 'paid', reference: text.trim() }
        : { id: p.id, action: 'cancel', note: text.trim() },
      {
        onSuccess: () => {
          toast.success(
            mode === 'paid'
              ? 'Marked sent. The partner was told.'
              : 'Cancelled. The amount is back in their balance.',
          )
          setMode(null)
        },
        onError: (err) => toast.error(errorMessage(err)),
      },
    )
  }

  return (
    <div className="srow" style={{ flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0 }}>
        <b>{p.partner?.name ?? 'Closed account'}</b>
        <small>
          {p.paypal_email} · asked {formatDate(p.requested_at)}
          {p.status === 'paid' &&
            ` · sent ${formatDate(p.paid_at)}${p.reference ? ` (${p.reference})` : ''} by ${p.handled_by ?? 'staff'}`}
          {p.status === 'cancelled' && ` · cancelled by ${p.handled_by ?? 'staff'}: ${p.note ?? ''}`}
        </small>
      </div>
      <div className="acts" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <b className="num">{formatUsd(p.amount)}</b>
        {p.status === 'requested' && canPay && mode === null ? (
          <>
            <button
              type="button"
              className="btn sm"
              onClick={() => {
                setMode('paid')
                setText('')
              }}
            >
              <Check aria-hidden="true" />
              Mark sent
            </button>
            <button
              type="button"
              className="btn sm ghost"
              onClick={() => {
                setMode('cancel')
                setText('')
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <span className={cn('chip', PAYOUT[p.status].tone)}>{PAYOUT[p.status].label}</span>
        )}
      </div>
      {mode && (
        <form
          onSubmit={submit}
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 8, width: '100%' }}
        >
          <label className="grid min-w-56 flex-1 gap-1 text-xs text-ink-muted">
            {mode === 'paid'
              ? 'PayPal transaction ID (optional)'
              : 'Why it’s cancelled (the partner sees this)'}
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              required={mode === 'cancel'}
              maxLength={mode === 'paid' ? 64 : 300}
            />
          </label>
          <Button type="submit" size="sm" loading={handle.isPending}>
            {mode === 'paid' ? 'Mark sent' : 'Cancel payout'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setMode(null)}>
            Back
          </Button>
        </form>
      )}
    </div>
  )
}
